import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import StopIcon from '@mui/icons-material/Stop';
import { Avatar, Box, Chip, Divider, IconButton, InputBase, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createContext, Dispatch, Fragment, SetStateAction, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { initials } from '@/utils/format';
import { ChatMessage, streamReply } from './assistantClient';

export const ASSISTANT_WIDTH = 400;
const OPEN_KEY = 'hrms.assistant.open';

const SUGGESTIONS = ['How many leaves do I have left?', 'Show my attendance this week', 'When is the next holiday?', 'Summarise pending approvals'];

interface AssistantContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** Kept here, not in the panel, so the conversation survives closing and reopening it. */
  messages: ChatMessage[];
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0');
    } catch {
      // Storage unavailable (private mode) — the toggle still works for this page load.
    }
  }, []);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const value = useMemo(() => ({ open, setOpen, toggle: () => setOpen(!open), messages, setMessages }), [open, setOpen, messages]);
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant() {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used within AssistantProvider');
  return ctx;
}

/** Renders **bold** spans and line breaks; enough for the assistant's short replies. */
function RichText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>,
      )}
    </>
  );
}

function TypingDots() {
  return (
    <Box aria-label="Assistant is typing" sx={{ display: 'flex', gap: 0.5, py: 1 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'text.secondary',
            animation: 'assistantDot 1.2s infinite ease-in-out',
            animationDelay: `${i * 0.15}s`,
            '@keyframes assistantDot': { '0%, 80%, 100%': { opacity: 0.25 }, '40%': { opacity: 1 } },
          }}
        />
      ))}
    </Box>
  );
}

function MessageRow({ message, pending }: { message: ChatMessage; pending: boolean }) {
  const { principal } = useAuth();
  if (message.role === 'user') {
    return (
      <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
        <Paper
          sx={{
            px: 1.75,
            py: 1.25,
            maxWidth: '85%',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
            borderRadius: 2.5,
            borderTopRightRadius: 6,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {message.content}
          </Typography>
        </Paper>
        <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.main' }}>{initials(principal?.displayName ?? '?')}</Avatar>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.5 }}>
        {pending && !message.content ? (
          <TypingDots />
        ) : (
          <Typography variant="body2" color={message.error ? 'error' : 'text.primary'} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.65 }}>
            <RichText text={message.content} />
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function EmptyState({ onPick }: { onPick: (text: string) => void }) {
  const { principal } = useAuth();
  const firstName = principal?.displayName?.split(' ')[0];
  return (
    <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', m: 'auto', px: 3, py: 4 }}>
      <Avatar sx={{ width: 48, height: 48, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: 'secondary.main' }}>
        <AutoAwesomeIcon />
      </Avatar>
      <Box>
        <Typography variant="h5">{firstName ? `Hi ${firstName}, how can I help?` : 'How can I help?'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Ask about leave, attendance, onboarding, assets or company policies.
        </Typography>
      </Box>
      <Stack spacing={1} sx={{ width: '100%' }}>
        {SUGGESTIONS.map((s) => (
          <Chip key={s} label={s} variant="outlined" onClick={() => onPick(s)} sx={{ justifyContent: 'flex-start', height: 'auto', py: 1, fontWeight: 500, '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' } }} />
        ))}
      </Stack>
    </Stack>
  );
}

function newId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

export function AssistantPanel() {
  const { setOpen, messages, setMessages } = useAssistant();
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => abortRef.current?.abort();
  }, []);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || streaming) return;
    const user: ChatMessage = { id: newId(), role: 'user', content, createdAt: Date.now() };
    const reply: ChatMessage = { id: newId(), role: 'assistant', content: '', createdAt: Date.now() };
    const history = [...messages, user];
    setMessages([...history, reply]);
    setInput('');
    setStreaming(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const patch = (fn: (m: ChatMessage) => ChatMessage) => setMessages((ms) => ms.map((m) => (m.id === reply.id ? fn(m) : m)));
    try {
      for await (const chunk of streamReply(history, controller.signal)) {
        patch((m) => ({ ...m, content: m.content + chunk }));
      }
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        patch((m) => ({ ...m, content: 'Something went wrong. Please try again.', error: true }));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  };

  const stop = () => abortRef.current?.abort();

  const reset = () => {
    stop();
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const last = messages[messages.length - 1];

  return (
    <Box component="section" aria-label="AI assistant" sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 2, minHeight: 64 }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: 'secondary.main' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          HRMS Assistant
        </Typography>
        <Tooltip title="New chat">
          <span>
            <IconButton size="small" onClick={reset} disabled={!messages.length} aria-label="New chat">
              <AddCommentOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close assistant">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Divider />
      <Box ref={scrollRef} aria-live="polite" sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {messages.length ? (
          <Stack spacing={2.5} sx={{ p: 2 }}>
            {messages.map((m) => (
              <MessageRow key={m.id} message={m} pending={streaming && m.id === last?.id} />
            ))}
          </Stack>
        ) : (
          <EmptyState onPick={send} />
        )}
      </Box>
      <Box sx={{ p: 2, pt: 1 }}>
        <Paper
          variant="outlined"
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            pl: 1.75,
            pr: 0.75,
            py: 0.75,
            borderRadius: 3,
            transition: 'border-color .15s, box-shadow .15s',
            '&:focus-within': { borderColor: 'primary.main', boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.12)}` },
          }}
        >
          <InputBase
            inputRef={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send(input);
              }
            }}
            placeholder="Ask anything about HR…"
            multiline
            maxRows={6}
            fullWidth
            inputProps={{ 'aria-label': 'Message the assistant' }}
            sx={{ fontSize: 14, py: 0.75 }}
          />
          {streaming ? (
            <Tooltip title="Stop">
              <IconButton onClick={stop} aria-label="Stop generating" sx={{ bgcolor: 'text.primary', color: 'background.paper', width: 32, height: 32, '&:hover': { bgcolor: 'text.secondary' } }}>
                <StopIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <IconButton
              type="submit"
              disabled={!input.trim()}
              aria-label="Send message"
              sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 32, height: 32, '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
            >
              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Paper>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ textAlign: 'center', mt: 1 }}>
          AI responses may be inaccurate. Verify important information.
        </Typography>
      </Box>
    </Box>
  );
}
